import { useCallback, useEffect, useState } from "react";
import { api, fmtErr, fmtDateTime } from "../../lib/api";
import { toast } from "sonner";
import { Bell, CheckCheck } from "lucide-react";

const KIND_LABELS = {
  registrazione: { label: "Nuova registrazione", cls: "text-emerald-300 border-emerald-500/40" },
  abbonamento_attivato: { label: "Abbonamento attivato", cls: "text-amber-300 border-amber-500/40" },
  rinnovo_abbonamento: { label: "Rinnovo riuscito", cls: "text-amber-200 border-amber-500/30" },
  pagamento_fallito: { label: "Pagamento non riuscito", cls: "text-red-300 border-red-500/40" },
  recensione_in_attesa: { label: "Recensione in attesa", cls: "text-sky-300 border-sky-500/40" },
  segnalazione: { label: "Nuova segnalazione", cls: "text-rose-300 border-rose-500/40" },
};

export default function AdminNotifiche() {
  const [data, setData] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/notifications/feed").then((r) => setData(r.data)).catch((e) => toast.error(fmtErr(e)));
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  const markRead = async (nid, already) => {
    if (already) return;
    try {
      await api.post(`/admin/notifications/${nid}/read`);
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const markAll = async () => {
    try {
      await api.post("/admin/notifications/read-all");
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const items = data?.items || [];

  return (
    <div data-testid="admin-notifiche-section">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-display text-2xl text-amber-100 mb-1">Avvisi</h2>
          <p className="text-xs text-purple-300/60">
            Registrazioni, abbonamenti, rinnovi, pagamenti, recensioni in attesa e segnalazioni — solo per l'amministratore, con push dedicata sul telefono. Aggiornamento automatico ogni minuto.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-[10px] uppercase tracking-wider border border-[#4a2175] rounded-full px-3 py-1.5 text-purple-300/60" data-testid="admin-notifiche-unread">
            {data ? `${data.unread} non lette` : "…"}
          </span>
          {data && data.unread > 0 && (
            <button onClick={markAll} data-testid="admin-notifiche-read-all"
              className="text-xs text-amber-200 border border-amber-500/40 rounded-full px-4 py-1.5 hover:bg-amber-500/10 transition-colors inline-flex items-center gap-1.5">
              <CheckCheck className="w-3.5 h-3.5" /> Segna tutte lette
            </button>
          )}
        </div>
      </div>

      {data === null ? (
        <p className="text-xs text-purple-300/50">Caricamento…</p>
      ) : items.length === 0 ? (
        <div className="mystic-card p-10 text-center" data-testid="admin-notifiche-empty">
          <Bell className="w-8 h-8 text-amber-400/50 mx-auto mb-3" />
          <p className="text-purple-200/60 text-sm">Nessun avviso: lo storico si popolerà con registrazioni, abbonamenti, rinnovi e pagamenti non riusciti.</p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="admin-notifiche-list">
          {items.map((n) => {
            const k = KIND_LABELS[n.kind] || { label: n.title || n.kind, cls: "text-purple-200 border-[#4a2175]" };
            return (
              <button key={n.id} onClick={() => markRead(n.id, n.read)} data-testid={`admin-notifica-${n.id}`}
                className={`w-full text-left border rounded-xl px-4 py-3 transition-colors ${n.read ? "border-[#4a2175]/50 opacity-70" : "border-amber-500/50 bg-amber-500/5"}`}>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                  <span className={`text-[10px] uppercase tracking-wider border rounded-full px-2.5 py-0.5 ${k.cls}`} data-testid={`admin-notifica-kind-${n.id}`}>{k.label}</span>
                  <span className="text-amber-100 text-sm font-medium">{n.user_name || "—"}</span>
                  <span className="text-amber-200/80 text-xs" data-testid={`admin-notifica-email-${n.id}`}>{n.user_email || "—"}</span>
                  <span className="text-[10px] uppercase tracking-wider border border-[#4a2175] rounded-full px-2 py-0.5 text-purple-200/80" data-testid={`admin-notifica-platform-${n.id}`}>{n.platform || "—"}</span>
                  {n.plan && <span className="text-xs text-purple-200/70">{n.plan}</span>}
                  {n.detail && <span className="text-[11px] text-purple-300/60 italic w-full" data-testid={`admin-notifica-detail-${n.id}`}>{n.detail}</span>}
                  <span className="ml-auto text-[11px] text-purple-300/50">{fmtDateTime(n.created_at)}</span>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
